export enum PSR_SUBMIT_TYPE {
	NORMAL = 'NORMAL',
	LEAVER = 'LEAVER',
}

export const psrPayrollScope = (isBureau: boolean) => ({
  paymentModels: isBureau ? ['PAYEE'] : ['PAYEE', 'UMBRELLA'],
  appName: isBureau ? 'BUREAU' : 'UMBRELLA',
});

export const prsiEeClasses = [
	{
		value: 'A0_A1',
		label: 'Class J0/A0/AX/AL/A1',
		desc: 'Employee aged 16 to 65',
	},
	{
		value: 'A8_A9',
		label: 'Class A8/A9',
		desc: 'Community Employment participant',
	},
	{
		value: 'J_16_65',
		label: 'Class J0/J1',
		desc: 'Employee aged 16 to 65 in subsidiary employment',
	},
	{
		value: 'J_OVER_65',
		label: 'Class J0/J1 (Over 65)',
		desc: 'Employee aged over 65',
	},
	{
		value: 'M_UNDER_15',
		label: 'Class M',
		desc: 'Employee aged 15 or under',
	},
	{
		value: 'M_PENSION',
		label: 'Class M (Pension)',
		desc: 'Employee in receipt of occupational pension',
	},
	{
		value: 'M_K1',
		label: 'Class M/K1',
		desc: 'Office holder',
	},
	{
		value: 'B',
		label: 'Class B',
		desc: 'Public sector employee',
	},
	{
		value: 'C',
		label: 'Class C',
		desc: 'Public sector employee',
	},
	{
		value: 'D',
		label: 'Class D',
		desc: 'Public sector employee',
	},
	{
		value: 'H',
		label: 'Class H',
		desc: 'Public sector employee',
	},
	{
		value: 'E',
		label: 'Class E',
		desc: 'Minister of religion employed by COI Representative Body',
	},
	{
		value: 'S',
		label: 'Class S',
		desc: 'Self-employed qualifying director',
	},
	{
		value: 'M_OVER_66',
		label: 'Class M (Over 66)',
		desc: 'Self-employed qualifying director aged 66 or over',
	},
];

export enum USC_EXEMPTION_TYPE {
	NOT_EXEMPT = 'NOT_EXEMPT',
	EXEMPT = 'EXEMPT',
}

export const uscExemptionOptions = [
	{ value: USC_EXEMPTION_TYPE.NOT_EXEMPT, label: 'Not Exempt from USC' },
	{ value: USC_EXEMPTION_TYPE.EXEMPT, label: 'Exempt from USC' },
];

export enum MEDICAL_INSURANCE_TYPE {
	ANNUAL = 'MEDICAL_INSURANCE_ANNUAL',
	ONE_OFF = 'MEDICAL_INSURANCE_ONE_OFF',
}

export const medicalInsuranceTypes = [
	{ label: 'Annual', value: MEDICAL_INSURANCE_TYPE.ANNUAL },
	{ label: 'One-off', value: MEDICAL_INSURANCE_TYPE.ONE_OFF },
];

// Mirrors the PSR list's Status column; the API filters on the same derivation.
export enum PSR_SUBMISSION_STATUS {
	SUCCESS = 'SUCCESS',
	UPDATED = 'UPDATED',
	WARNING = 'WARNING',
	FAILED = 'FAILED',
}

/**
 * NX-6140: what REVENUE did with a payslip, as opposed to whether our POST
 * succeeded.
 *
 * Revenue acknowledges a submission and validates it afterwards, so a payslip
 * can be accepted at the door and dropped on inspection. `isSuccess` only ever
 * meant "the HTTP call did not throw", which is why the old client-side status
 * showed a dropped payslip as green.
 *
 * Null on every row filed before this shipped - those have no verdict and must
 * keep rendering the way they always did.
 */
export enum PSR_REVENUE_STATUS {
	PENDING = 'PENDING',
	COMPLETED = 'COMPLETED',
	NOT_RECEIVED = 'NOT_RECEIVED',
	FAILED = 'FAILED',
	CHECK_FAILED = 'CHECK_FAILED',
	/** ROS "Active": processed, counts toward the run's PAYE totals. */
	ACTIVE = 'ACTIVE',
	/** ROS "Invalid": rejected, excluded from the run's PAYE totals. */
	INVALID = 'INVALID',
	WARNED = 'WARNED',
}

/**
 * Revenue's own words, so a row reads the same here as on the ROS portal.
 *
 * ROS labels a submission item Active | Inactive | Invalid | Deleted. We show
 * the first three; Deleted is not reachable, because the REST API never
 * reports it - a payslip deleted through the portal is invisible to us.
 *
 * The rest are OUR states, not Revenue's, and are deliberately worded so they
 * cannot be mistaken for a verdict: they say what we know, not what Revenue
 * decided. Revenue never sends an item status at all - a payslip is Active by
 * being ABSENT from invalidPayslips[], and Invalid by being named in it.
 */
export type PsrDisplayStatus =
	| 'Active'
	| 'Inactive'
	| 'Invalid'
	| 'Warning'
	| 'Failed'
	| 'Pending'
	| 'Not Received'
	| 'Not Confirmed'
	| 'Check Failed';

type PsrStatusInput = {
	revenueStatus?: string | null;
	isSuccess?: boolean;
	/**
	 * Kept on the input because callers still pass it, but it no longer changes
	 * the label: Revenue has no "Updated" state. A resubmitted row that Revenue
	 * accepted is Active, and the row it replaced is Inactive.
	 */
	isResubmitted?: boolean;
	/** A later submission replaced this row - ROS calls that Inactive. */
	isSuperseded?: boolean;
	hasPpsn?: boolean;
	/**
	 * The state of the message this payslip travelled in, when it had one.
	 *
	 * A payslip only gets its own verdict once Revenue has been asked AND
	 * answered. Until then the honest thing to show is what we know about the
	 * MESSAGE - "sent, not confirmed" - rather than falling through to the
	 * legacy rule, which reads `isSuccess` (the POST did not throw) as Success
	 * and would claim Revenue accepted a payslip nobody has asked about.
	 */
	batchStatus?: string | null;
};

/**
 * Revenue's answer wins; the old derivation is the fallback.
 *
 * Rows filed before NX-6140 have no revenueStatus, and there is no way to ask
 * Revenue about them retrospectively at scale, so they keep the historic
 * rendering rather than all turning into an "unknown" state.
 */
export const resolvePsrStatus = ({
	revenueStatus,
	isSuccess,
	hasPpsn,
	isSuperseded,
	batchStatus,
}: PsrStatusInput): PsrDisplayStatus => {
	// ROS: "Inactive - modified in a subsequent submission and is therefore not
	// included in the total PAYE deductions for this payroll run." It outranks
	// whatever verdict the row itself carried: once replaced, it stops counting
	// whether Revenue had accepted it or not.
	if (isSuperseded) return 'Inactive';

	switch (revenueStatus) {
		case PSR_REVENUE_STATUS.ACTIVE:
			// ROS: "Active - processed and included in the total PAYE deductions
			// for this payroll run." A resubmitted row that Revenue accepted is
			// still Active; the one it replaced is Inactive, handled below.
			return 'Active';
		case PSR_REVENUE_STATUS.WARNED:
			return 'Warning';
		case PSR_REVENUE_STATUS.INVALID:
			// ROS: "Invalid - contains an error and is therefore not included in
			// the total PAYE deductions for this payroll run."
			return 'Invalid';
		case PSR_REVENUE_STATUS.NOT_RECEIVED:
			return 'Not Received';
		case PSR_REVENUE_STATUS.CHECK_FAILED:
			return 'Check Failed';
		case PSR_REVENUE_STATUS.PENDING:
			return 'Pending';
		case PSR_REVENUE_STATUS.FAILED:
			return 'Failed';
		default:
			break;
	}

	if (!isSuccess) return 'Failed';

	// No verdict of its own, but we know what happened to the message it was in.
	// Showing Success here is what made the detail page contradict its own
	// banner: "could not confirm with Revenue" above three green rows.
	if (batchStatus === PSR_REVENUE_STATUS.CHECK_FAILED) return 'Not Confirmed';
	if (batchStatus === PSR_REVENUE_STATUS.PENDING) return 'Pending';
	if (batchStatus === PSR_REVENUE_STATUS.NOT_RECEIVED) return 'Not Received';
	if (batchStatus === PSR_REVENUE_STATUS.FAILED) return 'Failed';

	// Genuinely no batch: a row filed before submissions were batched. Those
	// never had a verdict and never will, so keep the historic rendering.
	if (!hasPpsn) return 'Warning';
	return 'Active';
};

/**
 * Resubmitting a payslip Revenue has not finished with would either duplicate
 * one it is about to accept or orphan a correction pointing at one it is about
 * to drop, so the button is hidden until there is a verdict. The API refuses it
 * too; this stops the user finding that out by clicking.
 */
export const canResubmitPsr = ({
	revenueStatus,
	isSuccess,
}: PsrStatusInput): boolean => {
	if (revenueStatus === PSR_REVENUE_STATUS.PENDING) return false;
	if (revenueStatus === PSR_REVENUE_STATUS.ACTIVE) return false;
	if (
		revenueStatus === PSR_REVENUE_STATUS.INVALID ||
		revenueStatus === PSR_REVENUE_STATUS.NOT_RECEIVED ||
		revenueStatus === PSR_REVENUE_STATUS.FAILED
	) {
		return true;
	}
	return !isSuccess;
};
